import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, ChevronDown, Mail, MessageCircle, Phone, UserCheck, Users, X } from "lucide-react";

const DEFAULT_RD_API_BASE_URL = "https://test.vdpl.co/api/index.php/";

const renderPortal = (node) => {
  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
};

const selectCls = (error, disabled = false) =>
  `w-full appearance-none rounded-xl border bg-white px-3.5 py-2.5 pr-10 text-sm text-slate-800 outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed ${error ? "border-red-300 bg-red-50/30" : "border-slate-200 hover:border-slate-300"} ${disabled ? "" : "cursor-pointer"}`;

const inputCls = (error) =>
  `w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 ${error ? "border-red-300 bg-red-50/30" : "border-slate-200 hover:border-slate-300"}`;

const getCandidateId = (candidate) =>
  String(candidate?.candidate_id ?? candidate?.candidateId ?? candidate?.id ?? "").trim();

const toEngagementForm = (candidate) => {
  const info = candidate?.candidateInfo || {};
  return {
    candidateConnect: String(candidate?.candidate_connect ?? candidate?.candidateConnect ?? info?.candidate_connect ?? info?.candidateConnect ?? "").trim(),
    candidateConnectVia: String(candidate?.connected_via ?? candidate?.candidate_connect_via ?? candidate?.candidateConnectVia ?? info?.connected_via ?? info?.candidate_connect_via ?? info?.candidateConnectVia ?? "").trim(),
    benchStatus: String(candidate?.bench_status_id ?? candidate?.benchStatusId ?? candidate?.benchStatus ?? info?.bench_status_id ?? info?.benchStatus ?? "").trim(),
    candidateResponse: String(candidate?.candidate_response ?? candidate?.candidateResponse ?? info?.candidate_response ?? info?.candidateResponse ?? "").trim(),
    comments: String(candidate?.comments ?? candidate?.candidate_comments ?? info?.comments ?? info?.rd_comments ?? "").trim(),
  };
};

const normalizeBenchStatusOptions = (payload) => {
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return rows
    .map((row) => ({
      id: String(row?.id ?? "").trim(),
      name: String(row?.status_name ?? row?.name ?? "").trim(),
    }))
    .filter((row) => row.id && row.name);
};

const CANDIDATE_RESPONSE_OPTIONS = ["Interested", "Not Interested", "Backout"];

const CandidateEngagementModal = ({
  isOpen,
  candidate,
  rdApiBaseUrl,
  onClose,
  onSaved,
  showRemoveBenchButton = false,
  onRemoveBench,
  removeBenchLoading = false,
}) => {
  const [form, setForm] = useState(() => toEngagementForm(candidate));
  const [errors, setErrors] = useState({});
  const [benchStatusOptions, setBenchStatusOptions] = useState([]);
  const [benchStatusLoading, setBenchStatusLoading] = useState(false);
  const [benchStatusError, setBenchStatusError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiBaseUrl = useMemo(() => {
    const raw = String(rdApiBaseUrl || DEFAULT_RD_API_BASE_URL || "").trim();
    if (!raw) return DEFAULT_RD_API_BASE_URL;
    return raw.endsWith("/") ? raw : `${raw}/`;
  }, [rdApiBaseUrl]);

  const benchStatusApiUrl = `${apiBaseUrl}bench_status`;
  const saveEngagementApiUrl = `${apiBaseUrl}setCandidateEngagement`;

  useEffect(() => {
    if (!isOpen) return;
    setForm(toEngagementForm(candidate));
    setErrors({});
    setSubmitError("");
  }, [candidate, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const ctrl = new AbortController();
    (async () => {
      setBenchStatusLoading(true);
      setBenchStatusError("");
      try {
        const response = await fetch(benchStatusApiUrl, { signal: ctrl.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        setBenchStatusOptions(normalizeBenchStatusOptions(payload));
      } catch (error) {
        if (error?.name !== "AbortError") setBenchStatusError("Unable to load response status options.");
      } finally {
        setBenchStatusLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [isOpen, benchStatusApiUrl]);

  const immediateOptions = useMemo(() => {
    return benchStatusOptions.filter((option) => {
      const name = option.name.toLowerCase();
      return name.includes("immediately") && !name.includes("not");
    });
  }, [benchStatusOptions]);

  const visibleBenchStatusOptions = useMemo(() => {
    const connect = String(form?.candidateConnect || "").trim().toLowerCase();
    if (connect === "yes") return immediateOptions;
    if (connect === "no") {
      return benchStatusOptions.filter((option) => {
        const name = option.name.toLowerCase();
        return !name.includes("immediately") && !name.includes("closure");
      });
    }
    return benchStatusOptions;
  }, [benchStatusOptions, form?.candidateConnect, immediateOptions]);

  useEffect(() => {
    if (!isOpen || !form?.benchStatus || !benchStatusOptions.length) return;
    const match = benchStatusOptions.find((option) => option.id === String(form.benchStatus) || option.name.toLowerCase() === String(form.benchStatus).toLowerCase());
    if (match && match.id !== String(form.benchStatus)) {
      setForm((prev) => ({ ...prev, benchStatus: match.id }));
    }
  }, [isOpen, form?.benchStatus, benchStatusOptions]);

  useEffect(() => {
    if (!isOpen || !form?.benchStatus) return;
    const isValid = visibleBenchStatusOptions.some((option) => option.id === String(form.benchStatus));
    if (!isValid) setForm((prev) => ({ ...prev, benchStatus: "" }));
  }, [isOpen, form?.candidateConnect, form?.benchStatus, visibleBenchStatusOptions]);

  useEffect(() => {
    if (!isOpen) return;
    const connect = String(form?.candidateConnect || "").trim().toLowerCase();
    if (connect === "yes") {
      setForm((prev) => (prev.benchStatus ? { ...prev, benchStatus: "" } : prev));
      return;
    }
    setForm((prev) => (prev.candidateResponse ? { ...prev, candidateResponse: "" } : prev));
  }, [isOpen, form?.candidateConnect]);

  const validate = () => {
    const next = {};
    const connect = String(form?.candidateConnect || "").trim().toLowerCase();
    if (!String(form?.candidateConnect || "").trim()) next.candidateConnect = "Candidate Connect is required";
    if (["yes", "no"].includes(String(form?.candidateConnect || "").trim().toLowerCase()) && !String(form?.candidateConnectVia || "").trim()) {
      next.candidateConnectVia = "Connected Via is required";
    }
    if (connect === "yes") {
      if (!String(form?.candidateResponse || "").trim()) next.candidateResponse = "Candidate Response is required";
    } else if (connect === "no" && !String(form?.benchStatus || "").trim()) {
      next.benchStatus = "Candidate Response is required";
    }
    return next;
  };

  const handleSave = async () => {
    const candidateId = getCandidateId(candidate);
    if (!candidateId) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitError("");
    setIsSubmitting(true);
    try {
      const user = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user") || "null") : null;
      const connect = String(form?.candidateConnect || "").trim().toLowerCase();
      const selectedStatus = benchStatusOptions.find((option) => option.id === String(form.benchStatus));
      const candidateResponseValue = connect === "yes"
        ? String(form?.candidateResponse || "").trim()
        : String(selectedStatus?.name || "").trim();
      const benchStatusIdValue = connect === "yes" ? "" : String(form?.benchStatus || "").trim();
      const trimmedComments = String(form.comments || "").trim();
      const body = new URLSearchParams();
      body.append("candidate_id", candidateId);
      body.append("candidate_connect", String(form.candidateConnect || "").trim());
      body.append("connected_via", String(form.candidateConnectVia || "").trim());
      body.append("bench_status_id", benchStatusIdValue);
      body.append("candidate_response", candidateResponseValue);
      body.append("candidate_comments", trimmedComments);
      body.append("updated_by", String(user?.id || user?.user_id || user?.userId || "").trim());

      const response = await fetch(saveEngagementApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: body.toString(),
      });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      let payload = null;
      try { payload = await response.json(); } catch { payload = null; }
      const failed = payload?.success === false || String(payload?.status || "").toLowerCase() === "error";
      if (failed) throw new Error(payload?.message || "Failed to save engagement.");
      onSaved?.({
        candidateId,
        candidateConnect: String(form.candidateConnect || "").trim(),
        connectedVia: String(form.candidateConnectVia || "").trim(),
        benchStatusId: benchStatusIdValue,
        candidateResponse: candidateResponseValue,
        comments: trimmedComments,
      });
      onClose?.();
    } catch (error) {
      setSubmitError(error?.message || "Unable to save engagement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const candidateName = String(candidate?.name || "").trim() || "Candidate";
  const candidateTitle = String(candidate?.title || candidate?.headline || "").trim() || "Job title not available";
  const connectValue = String(form.candidateConnect || "").trim().toLowerCase();
  const showConnectVia = ["yes", "no"].includes(connectValue);
  const isConnectYes = connectValue === "yes";

  return renderPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-lg font-black text-slate-900">Candidate Engagement</h3>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {candidateName} - {candidateTitle}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {showRemoveBenchButton && (
              <button
                type="button"
                onClick={onRemoveBench}
                disabled={removeBenchLoading}
                className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-all disabled:opacity-60"
              >
                <UserCheck className="h-3.5 w-3.5" />
                {removeBenchLoading ? "Saving..." : "Remove Bench"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="max-h-[66vh] overflow-y-auto px-6 py-5 space-y-4 bg-slate-50/40">
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-bold text-slate-900">Candidate Engagement</h4>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Candidate Connect <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={form.candidateConnect}
                    onChange={(event) => setForm((prev) => ({ ...prev, candidateConnect: event.target.value }))}
                    className={selectCls(errors.candidateConnect)}
                  >
                    <option value="">Select Candidate Connect</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
                {errors.candidateConnect && (
                  <p className="mt-1 text-[11px] text-red-600">{errors.candidateConnect}</p>
                )}
              </div>

              {showConnectVia && (
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Connected Via <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ value: "Phone", Icon: Phone }, { value: "Email", Icon: Mail }].map(({ value, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, candidateConnectVia: prev.candidateConnectVia === value ? "" : value }))}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all ${
                          form.candidateConnectVia === value
                            ? "border-blue-500 bg-blue-600 text-white shadow-sm"
                            : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {value}
                      </button>
                    ))}
                  </div>
                  {errors.candidateConnectVia && (
                    <p className="mt-1 text-[11px] text-red-600">{errors.candidateConnectVia}</p>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Candidate Response <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={isConnectYes ? form.candidateResponse : form.benchStatus}
                    onChange={(event) => setForm((prev) => (isConnectYes ? { ...prev, candidateResponse: event.target.value } : { ...prev, benchStatus: event.target.value }))}
                    disabled={!isConnectYes && benchStatusLoading}
                    className={selectCls(isConnectYes ? errors.candidateResponse : errors.benchStatus, !isConnectYes && benchStatusLoading)}
                  >
                    <option value="">{!isConnectYes && benchStatusLoading ? "Loading..." : "Select Response"}</option>
                    {isConnectYes
                      ? CANDIDATE_RESPONSE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))
                      : visibleBenchStatusOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.name}</option>
                      ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
                {isConnectYes && errors.candidateResponse && <p className="mt-1 text-[11px] text-red-600">{errors.candidateResponse}</p>}
                {!isConnectYes && errors.benchStatus && <p className="mt-1 text-[11px] text-red-600">{errors.benchStatus}</p>}
                {!isConnectYes && benchStatusError && <p className="mt-1 text-[11px] text-red-600">{benchStatusError}</p>}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-indigo-600" />
              <h4 className="text-sm font-bold text-slate-900">Comments</h4>
            </div>
            <textarea
              rows={5}
              value={form.comments}
              onChange={(event) => setForm((prev) => ({ ...prev, comments: event.target.value }))}
              placeholder="Add engagement notes or follow-up comments..."
              className={inputCls()}
            />
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-white px-6 py-4">
          <div>
            {submitError && (
              <p className="inline-flex items-center gap-1.5 text-xs text-red-600">
                <AlertCircle className="h-3.5 w-3.5" />
                {submitError}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {isSubmitting ? "Saving..." : "Save Engagement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateEngagementModal;
